/**
 * StatusIndicator Component - Reusable status indicator with dot and text
 * 
 * Features:
 * - Colored dot with halo effect based on status
 * - Color-coordinated text matching the dot color
 * - Support for online/offline status visualization
 * - Consistent styling across the application
 * 
 * @author CTS - Ford PM UI Team
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

const StatusIndicator = ({ status, size = 'medium' }) => {
  // Define sizes
  const sizes = {
    small: { halo: '12px', dot: '6px', fontSize: '10px' },
    medium: { halo: '15px', dot: '8px', fontSize: '12px' },
    large: { halo: '20px', dot: '10px', fontSize: '14px' },
    full: { halo: '12px', dot: '12px', fontSize: '12px' }
  };
  
  const currentSize = sizes[size] || sizes.medium;
  
  // Define colors for all statuses
  const colors = {
    online: {
      halo: 'rgba(34, 197, 94, 0.15)',
      dot: '#22c55e',
      text: '#22c55e'
    },
    offline: {
      halo: 'rgba(239, 68, 68, 0.15)',
      dot: '#ef4444', 
      text: '#ef4444'
    },
    ongoing: {
      halo: 'rgba(251, 191, 36, 0.15)',
      dot: '#f59e0b',
      text: '#d97706'
    },
    completed: {
      halo: 'rgba(34, 197, 94, 0.15)',
      dot: '#10b981',
      text: '#059669'
    }
  };
  
  // Determine status type
  const getStatusType = (statusText) => {
    if (!statusText) return 'offline';
    const status = statusText.toLowerCase();
    if (status.includes('online') || status.includes('active') || status.includes('connected')) return 'online';
    if (status.includes('offline') || status.includes('down') || status.includes('failed') || status.includes('power loss')) return 'offline';
    if (status.includes('ongoing') || status.includes('progress') || status.includes('pending')) return 'ongoing';
    if (status.includes('completed') || status.includes('success') || status.includes('done') || status.includes('power restored')) return 'completed';
    return 'offline'; // default
  };
  
  const currentColors = colors[getStatusType(status)];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Halo Circle with Dot */}
      <Box
        sx={{
          position: 'relative',
          width: currentSize.halo,
          height: currentSize.halo,
          borderRadius: '50%',
          backgroundColor: currentColors.halo,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Solid Dot */}
        <Box
          sx={{
            width: currentSize.dot,
            height: currentSize.dot,
            borderRadius: '50%',
            backgroundColor: currentColors.dot,
          }}
        />
      </Box>
      
      {/* Status Text */}
      <Typography sx={{ 
        fontSize: currentSize.fontSize,
        fontWeight: 700,
        color: currentColors.text,
        textTransform: 'capitalize'
      }}>
        {status}
      </Typography>
    </Box>
  );
};

StatusIndicator.propTypes = {
  status: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large'])
};

StatusIndicator.defaultProps = {
  size: 'medium'
};

export default StatusIndicator;
